'use client';

import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ArrowRight, ArrowUpRight, Building2, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';
import { cn } from '@/lib/utils';
import type {
  IndustryCategoryDetailResponse,
  IndustryCategoryTopCompanyItem,
} from '@/lib/backend-api';
import { getPublicStorageUrl } from '@/lib/public-storage-url';
import { generateCompanySlug } from '@/lib/company-utils';
import { localizePathname, type Locale } from '@/lib/i18n-routes';
import { generateCategoryInternalPath, generateIndustryUrl } from '@/lib/industry-utils';
import { getCategoryLabel } from '@/lib/industry-categories';
import { useLocale } from '@/hooks/use-locale';
import { createFormatters } from '@/new_components/company-detail/currencyFormatters';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer } from '@/components/ui/chart';
import { Container } from '@/components/common/container';
import { LocaleLink } from '@/components/locale-link';

interface NaceTreeNode {
  code: string;
  description: string;
  description_en: string | null;
  level: number;
}

interface Props {
  data: IndustryCategoryDetailResponse;
  /** Full WZ 2025 hierarchy under the subcategory's divisions; passed only
   * for subcategory pages where there's no nested industry_category data. */
  naceTree?: NaceTreeNode[];
}

const PIE_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#f59e0b',
  '#10b981',
  '#06b6d4',
  '#ef4444',
  '#84cc16',
  '#f97316',
  '#6366f1',
];

export default function CategoryDetailClient({ data, naceTree }: Props) {
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();
  const router = useRouter();
  const fmt = useMemo(() => createFormatters(i18n.language), [i18n.language]);

  const {
    category,
    parent_info,
    children,
    top_companies,
    keywords = [],
  } = data;
  const isSub = category.parent !== null;

  const pieData = useMemo(() => {
    const companies = top_companies
      .filter(
        (c): c is IndustryCategoryTopCompanyItem & { revenue: number; market_share: number } =>
          (c.revenue ?? 0) > 0 && (c.market_share ?? 0) > 0,
      )
      .slice(0, 10);
    const topShare = companies.reduce((sum, c) => sum + c.market_share, 0);
    const othersShare = Math.max(0, 100 - topShare);
    const slices: {
      name: string;
      value: number;
      revenue: number;
      color: string;
      href: string | null;
      logoUrl: string | null;
      isMore?: boolean;
    }[] = companies.map((c, i) => ({
      name: c.name ?? c.eu_id,
      value: Math.round(c.market_share * 100) / 100,
      revenue: c.revenue,
      color: PIE_COLORS[i % PIE_COLORS.length],
      href: generateCompanySlug({
        name: c.name ?? '',
        registerCourt: c.register_court,
        registerType: c.register_type,
        registerId: c.register_id,
      }, locale as Locale),
      logoUrl: c.favicon_storage_key
        ? getPublicStorageUrl(c.favicon_storage_key) ?? null
        : null,
    }));
    if (othersShare > 0.5) {
      // The /search `industry` filter only accepts top-level category slugs.
      // For subcategories, expand to the underlying NACE divisions instead so
      // the filter is precise to the subcategory rather than broadened to its
      // parent.
      const searchBase = localizePathname('/search', locale as Locale);
      let href: string;
      if (category.parent !== null) {
        const params = new URLSearchParams();
        for (const code of category.nace_codes ?? []) {
          params.append('naceCode', String(code).padStart(2, '0'));
        }
        href = `${searchBase}?${params.toString()}`;
      } else {
        href = `${searchBase}?industry=${encodeURIComponent(category.category)}`;
      }
      slices.push({
        name: locale === 'de' ? 'Mehr anzeigen' : 'See More',
        value: Math.round(othersShare * 100) / 100,
        revenue: 0,
        color: '#94a3b8',
        href,
        logoUrl: null,
        isMore: true,
      });
    }
    return slices;
  }, [top_companies, locale, category.category, category.parent, category.nace_codes]);

  return (
    <>
    <Container className="pt-4 pb-2">
      <nav
        className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground"
        aria-label="Breadcrumb"
      >
        <LocaleLink
          href="/industry"
          className="hover:text-foreground transition-colors"
        >
          {locale === 'de' ? 'Branchen' : 'Industries'}
        </LocaleLink>
        {parent_info && (
          <>
            <ChevronRight size={14} className="text-muted-foreground/50" />
            <LocaleLink
              href={generateCategoryInternalPath(parent_info.category, locale)}
              className="hover:text-foreground transition-colors"
            >
              {getCategoryLabel(parent_info.category, locale)}
            </LocaleLink>
          </>
        )}
        <ChevronRight size={14} className="text-muted-foreground/50" />
        <span className="text-foreground font-medium">{getCategoryLabel(category.category, locale)}</span>
      </nav>
    </Container>
    <Container className="pb-12 flex flex-col gap-10">
      <div className="flex flex-col gap-3 pr-5 py-3">
        <h1 className="text-base sm:text-2xl font-bold tracking-tight text-foreground">
          {getCategoryLabel(category.category, locale)}
        </h1>
        {(locale === 'de' ? category.description_de : category.description_en) && (
          <p className="text-muted-foreground text-base max-w-3xl font-light">
            {locale === 'de' ? category.description_de : category.description_en}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1.5">
              {t('common.labels.revenue')}
            </span>
            <span className="font-medium">
              {fmt.compactCurrency.format(category.total_revenue)}
            </span>
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1.5">
              {t('common.labels.profit')}
            </span>
            <span
              className={cn(
                'font-medium',
                category.total_profit >= 0
                  ? 'text-green-700 dark:text-green-500'
                  : 'text-red-700 dark:text-red-500',
              )}
            >
              {fmt.compactCurrency.format(category.total_profit)}
            </span>
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1.5">
              {t('common.labels.companies')}
            </span>
            <span className="font-medium">
              {category.company_count.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {isSub && naceTree && naceTree.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">
            {locale === 'de' ? 'WZ 2025 Codes' : 'WZ 2025 codes'}
          </h2>
          <ul className="flex flex-col gap-0.5 border border-black/5 rounded-xl bg-card/40 p-2">
            {naceTree.map((node) => {
              const desc =
                (locale === 'en' && node.description_en) || node.description;
              const href = generateIndustryUrl(node.code, desc, locale);
              const indent = (node.level - 2) * 16;
              const isLeaf = node.level === 5;
              return (
                <li key={node.code}>
                  <LocaleLink
                    href={href}
                    style={{ paddingLeft: indent + 8 }}
                    className={cn(
                      'group flex items-baseline gap-3 py-1 pr-2 rounded hover:bg-muted/40 transition-colors',
                      isLeaf
                        ? 'text-xs text-muted-foreground'
                        : 'text-sm text-foreground',
                    )}
                  >
                    <span
                      className={cn(
                        'font-mono shrink-0 tabular-nums',
                        isLeaf ? 'w-16' : 'w-14 font-medium',
                      )}
                    >
                      {node.code}
                    </span>
                    <span className="group-hover:text-primary transition-colors">
                      {desc}
                    </span>
                  </LocaleLink>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {!isSub && children.filter((c) => c.company_count > 0).length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">
            {locale === 'de' ? 'Unterbranchen' : 'Subcategories'}
          </h2>
          <div className="flex flex-col gap-2">
            {children.filter((c) => c.company_count > 0).map((child) => (
              <LocaleLink
                key={child.category}
                href={generateCategoryInternalPath(child.category, locale)}
                className="group flex items-center py-2 px-4 rounded-xl hover:bg-muted/30 transition-all duration-300 border border-black/5 bg-card/40"
              >
                <div className="flex-grow min-w-0 mr-6">
                  <h3 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                    {getCategoryLabel(child.category, locale)}
                  </h3>
                  {(locale === 'de' ? child.description_de : child.description_en) && (
                    <p className="text-xs text-muted-foreground/80 mt-0.5 line-clamp-2">
                      {locale === 'de' ? child.description_de : child.description_en}
                    </p>
                  )}
                </div>
                <div className="hidden lg:flex flex-shrink-0 items-center gap-0 pr-4">
                  <div className="w-[100px] text-right">
                    <p className="text-[7px] uppercase font-medium text-muted-foreground tracking-wider">
                      {t('common.labels.revenue')}
                    </p>
                    <p className="text-sm font-medium tracking-tight">
                      {fmt.compactCurrency.format(child.total_revenue)}
                    </p>
                  </div>
                  <div className="w-[80px] text-right">
                    <p className="text-[7px] uppercase font-medium text-muted-foreground tracking-wider">
                      {t('common.labels.companies')}
                    </p>
                    <p className="text-sm font-medium tracking-tight text-foreground/70">
                      {child.company_count.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="lg:hidden flex items-center gap-2 text-[11px] mr-3">
                  <span className="text-foreground/70">
                    {fmt.compactCurrency.format(child.total_revenue)}
                  </span>
                  <span className="text-muted-foreground/40">·</span>
                  <span className="text-muted-foreground">
                    {child.company_count.toLocaleString()}
                  </span>
                </div>
                <div className="size-7 rounded-full bg-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                  <ArrowRight size={12} />
                </div>
              </LocaleLink>
            ))}
          </div>
        </section>
      )}

      {pieData.length > 1 && (
        <Card variant="grid">
          <CardHeader className="pb-2">
            <CardTitle as="h2">
              {locale === 'de' ? 'Marktanteil' : 'Market Share'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-[200px] h-[200px] shrink-0">
                <ChartContainer config={{}} className="w-full h-full">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius="55%"
                      outerRadius="80%"
                      dataKey="value"
                      strokeWidth={2}
                      stroke="var(--background)"
                      isAnimationActive={false}
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="bg-background border border-border rounded-lg p-3 shadow-xl text-xs">
                            <p className="font-semibold text-foreground mb-1 max-w-[180px] truncate">
                              {d.name}
                            </p>
                            <p className="text-muted-foreground">
                              {d.value.toFixed(2)}%{' '}
                              {locale === 'de' ? 'der Branche' : 'of sector'}
                            </p>
                            {d.revenue > 0 && (
                              <p className="text-primary font-medium">
                                {fmt.compactCurrency.format(d.revenue)}
                              </p>
                            )}
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ChartContainer>
              </div>
              <div className="w-full grid grid-cols-1 gap-y-2">
                {pieData.map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 min-w-0">
                    {entry.logoUrl ? (
                      <img
                        src={entry.logoUrl}
                        alt=""
                        className="flex-shrink-0 w-4 h-4 rounded-sm object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove(
                            'hidden',
                          );
                        }}
                      />
                    ) : null}
                    <Building2
                      size={14}
                      className={cn(
                        'flex-shrink-0 text-muted-foreground',
                        entry.logoUrl && 'hidden',
                      )}
                    />
                    {entry.isMore && entry.href ? (
                      <LocaleLink
                        href={entry.href}
                        className="text-xs text-foreground/70 truncate flex-grow underline hover:text-primary transition-colors inline-flex items-center gap-1"
                      >
                        {entry.name}
                        <ArrowUpRight size={12} className="flex-shrink-0" />
                      </LocaleLink>
                    ) : entry.href ? (
                      <Link
                        href={entry.href}
                        className="text-xs text-foreground/70 truncate flex-grow hover:text-primary hover:underline transition-colors"
                      >
                        {entry.name}
                      </Link>
                    ) : (
                      <span className="text-xs text-foreground/70 truncate flex-grow">
                        {entry.name}
                      </span>
                    )}
                    <span className="flex-shrink-0 text-xs font-semibold text-foreground tabular-nums">
                      {entry.value.toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {keywords.length > 0 && (() => {
        const TOP = 8;
        const groups: { cat: 'product' | 'risk' | 'opportunity'; label: string; route: string; color: string }[] = [
          {
            cat: 'product',
            label: locale === 'de' ? 'Produkte' : 'Products',
            route: '/products',
            color: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
          },
          {
            cat: 'risk',
            label: locale === 'de' ? 'Risiken' : 'Risks',
            route: '/risks',
            color: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
          },
          {
            cat: 'opportunity',
            label: locale === 'de' ? 'Chancen' : 'Opportunities',
            route: '/opportunities',
            color: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300',
          },
        ];
        const sections = groups
          .map((g) => ({
            ...g,
            items: keywords.filter((k) => k.category === g.cat).slice(0, TOP),
          }))
          .filter((g) => g.items.length > 0);
        if (sections.length === 0) return null;
        return (
          <Card variant="grid">
            <CardHeader className="pb-2">
              <CardTitle as="h2">
                {locale === 'de' ? 'Branchen-Keywords' : 'Industry Keywords'}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {sections.map(({ cat, label, items, route, color }) => (
                <div key={cat}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                    {label}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {/* onClick navigation (not <a href>) so crawlers don't
                        discover the noindexed keyword pages (GSC soft-404
                        noise) */}
                    {items.map((k) => (
                      <button
                        type="button"
                        key={k.keyword}
                        onClick={() =>
                          router.push(
                            localizePathname(
                              `${route}/${encodeURIComponent(k.keyword)}`,
                              locale as Locale,
                            ),
                          )
                        }
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium hover:opacity-80 transition-opacity ${color}`}
                      >
                        {k.keyword}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })()}

    </Container>
    </>
  );
}
